import {
  usePaymentInstrumentsCreateCreditCardRecaptchaV2Mutation,
  usePaymentInstrumentsCreateCreditCardRecaptchaV3Mutation,
  usePaymentInstrumentsCreateOtherMutation,
} from "@/hooks";
import config from "@/lib/config";
import { PAYMENT_METHOD_ID } from "@/lib/constants";
import routes from "@/lib/routes";
import { useCallback, useRef } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

export function useCreatePaymentInstrument({ creditCard = {}, other = {} }) {
  const [
    createCreditCardRecaptchaV2,
  ] = usePaymentInstrumentsCreateCreditCardRecaptchaV2Mutation(creditCard);
  const [
    createCreditCardRecaptchaV3,
  ] = usePaymentInstrumentsCreateCreditCardRecaptchaV3Mutation(creditCard);

  const [
    createOther,
    { data: createOtherData },
  ] = usePaymentInstrumentsCreateOtherMutation(other);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const tdsInfoToken = useRef();
  const tdsInfoTokenKey = useRef();

  const generateTokenCallback = useCallback(
    async ({ response, recaptchaType, recaptchaToken }) => {
      if (response.result == "OK") {
        const token = response.tokenResponse.token;
        const tokenKey = response.tokenResponse.tokenKey;
        const successUrl =
          typeof window != "undefined" &&
          `${window.location.origin}${routes.sbps3dsSuccess}`;
        const cancelUrl =
          typeof window != "undefined" && `${window.location.origin}`;
        const errorUrl =
          typeof window != "undefined" &&
          `${window.location.origin}${routes.sbps3dsError}`;
        const payload = creditCard?.payload || {};
        if (recaptchaType === "v2") {
          createCreditCardRecaptchaV2({
            variables: {
              payment_method_id: PAYMENT_METHOD_ID.CREDIT_CARD_3DS,
              replace_flag: false,
              success_url: encodeURIComponent(successUrl),
              cancel_url: encodeURIComponent(cancelUrl),
              error_url: encodeURIComponent(errorUrl),
              token: token,
              token_key: tokenKey,
              tds_info_token: tdsInfoToken.current,
              tds_info_token_key: tdsInfoTokenKey.current,
              tokenPayload: { ...payload },
              recaptchaTokenV2: recaptchaToken,
            },
          });
        } else if (recaptchaType === "v3") {
          recaptchaToken = await executeRecaptcha("payment_add");
          createCreditCardRecaptchaV3({
            variables: {
              payment_method_id: PAYMENT_METHOD_ID.CREDIT_CARD_3DS,
              replace_flag: false,
              success_url: encodeURIComponent(successUrl),
              cancel_url: encodeURIComponent(cancelUrl),
              error_url: encodeURIComponent(errorUrl),
              token: token,
              token_key: tokenKey,
              tds_info_token: tdsInfoToken.current,
              tds_info_token_key: tdsInfoTokenKey.current,
              tokenPayload: { ...payload },
              recaptchaTokenV3: recaptchaToken,
            },
          });
        }
      } else {
        if (creditCard?.onError) {
          creditCard.onError("入力内容に誤りがあります。");
        }
      }
    },
    [createCreditCardRecaptchaV2, createCreditCardRecaptchaV3, executeRecaptcha]
  );

  const generateTokenCallback3DS = useCallback((response) => {
    if (response.result == "OK") {
      const token = response.tokenResponse.tds2infoToken;
      const tokenKey = response.tokenResponse.tds2infoTokenKey;
      tdsInfoToken.current = token;
      tdsInfoTokenKey.current = tokenKey;
    } else {
      if (creditCard?.onError) {
        creditCard.onError("入力内容に誤りがあります。");
      }
    }
  });

  const createPaymentInstrument = useCallback(
    async (data) => {
      if (data?.cardNumber) {
        data.cardNumber = data?.cardNumber.replace(/-/g, "");
      }
      const {
        paymentMethod,
        expirationDate,
        cardNumber,
        cvv,
        firstName,
        lastName,
        mailAddress,
        recaptchaType,
        recaptchaToken,
      } = data;
      if (paymentMethod === PAYMENT_METHOD_ID.CREDIT_CARD_3DS) {
        if (recaptchaType !== "v2" && recaptchaType !== "v3") {
          creditCard.onError("recaptchaType is invalid");
        }

        if (typeof com_sbps_system_tds2 != "undefined") {
          com_sbps_system_tds2.generateToken(
            {
              merchantId: config.sbps.merchantId,
              serviceId: config.sbps.serviceId,
              billingLastName: lastName,
              billingFirstName: firstName,
              email: mailAddress,
            },
            (response) => generateTokenCallback3DS(response)
          );
        }
        if (typeof com_sbps_system != "undefined") {
          com_sbps_system.generateToken(
            {
              merchantId: config.sbps.merchantId,
              serviceId: config.sbps.serviceId,
              ccNumber: cardNumber,
              ccExpiration: expirationDate,
              securityCode: cvv,
            },
            (response) =>
              generateTokenCallback({
                response,
                recaptchaType,
                recaptchaToken,
              })
          );
        }
      } else {
        const successUrl =
          typeof window != "undefined" &&
          `${window.location.origin}${routes.sbpsSuccess}`;
        const cancelUrl =
          typeof window != "undefined" && window.location.origin;
        const errorUrl =
          typeof window != "undefined" &&
          `${window.location.origin}${routes.sbpsError}`;
        const payload = other?.payload || {};
        await createOther({
          variables: {
            payment_method_id: paymentMethod,
            replace_flag: false,
            success_url: encodeURIComponent(successUrl),
            cancel_url: encodeURIComponent(cancelUrl),
            error_url: encodeURIComponent(errorUrl),
            tokenPayload: { ...payload, paymentMethodId: paymentMethod },
          },
        });
      }
    },
    [createOther, generateTokenCallback]
  );

  return {
    createPaymentInstrument,
    createOtherData: createOtherData?.paymentInstrumentsCreateOther,
  };
}
